import type { AdminReplacementSummary } from '@/api/remplacements';

export interface AdminEfpOrderVendeur {
  id_vendeur: number;
  nomBoutique?: string | null;
  nomSociete?: string | null;
  nomContact?: string | null;
  prenomContact?: string | null;
  email?: string | null;
  email2?: string | null;
  statut?: number | null;
  note?: number | null;
  siret?: string | null;
  tva?: string | null;
}

export interface AdminEfpOrderAcheteur {
  id_acheteur: number;
  nomSociete?: string | null;
  nomContact?: string | null;
  prenomContact?: string | null;
  email: string;
  site_web?: string | null;
  tva_intra?: string | null;
  statut: boolean;
  newsletter: boolean;
  dateCreation: string;
  in_tmp?: boolean;
}

export interface AdminEfpOrderAdminCommentaire {
  id_commande_commentaire: number;
  id_commande: number;
  id_adminuser: number;
  commentaire: string;
  date_creation: string;
  prenom?: string | null;
}

export interface AdminEfpOrderPaiement {
  id_paiement: number | null;
  libelle: string;
  isHipay: boolean;
  isScalapay?: boolean;
  isVirementManuel: boolean;
  argentRecu: boolean;
  statut_paiement: string | null;
  statut_paiement_libelle: string;
  statut_paiement_display: string;
  statutPaiementRecu: boolean;
  certificat_paiement: string | null;
  montantGroupe: number | null;
  fraisPercent?: number | null;
  fraisMontant?: number | null;
  fraisLibelle?: string | null;
  montantHorsFraisScalapay?: number | null;
}

export interface AdminEfpOrderCalculs {
  ht: number;
  htDisplay: number;
  montantCA: number;
  ttc: number | null;
  poids: number;
  duCmdHt: number | null;
  duCmdTtc: number | null;
  isEtranger: boolean;
}

export interface AdminEfpOrderGroupeCalculs {
  payer: number;
  portHt: number;
  portTtc: number | null;
  duPortHt: number;
  duPortTtc: number | null;
  isEtranger: boolean;
}

export interface AdminEfpOrderPaysLivraison {
  id_pays: number | null;
  nom: string | null;
  code: string | null;
  codePostal?: string | null;
}

export interface AdminEfpOrderPromoCode {
  code: string;
  montant: number;
  type: string;
  libelleRemise?: string | null;
  scope: 'fraisport' | 'commande';
}

export interface AdminEfpOrder {
  remplacements?: AdminReplacementSummary;
  id_commande: number;
  id_commande_name: string;
  id_commande_groupe: number | null;
  id_commande_groupe_name?: string | null;
  montantTotal: number;
  montantApresRemise: number | null;
  montantCA: number;
  frais_port_src: number | null;
  frais_port: number | null;
  nb_colis: number;
  statut: number | null;
  id_livraison: number | null;
  dateCreation: string;
  dateCommande: string;
  dateExpedition: string | null;
  raisonAnnulationCode?: string | null;
  raisonAnnulationDetail?: string | null;
  annulationAuteurType?: "vendeur" | "admin" | null;
  annulationAuteurNom?: string | null;
  annulationAuteurEmail?: string | null;
  etiquetteUrl?: string | null;
  groupage?: boolean;
  commentaires?: string | null;
  commentairesAdmin?: AdminEfpOrderAdminCommentaire[];
  paiement?: AdminEfpOrderPaiement;
  calculs?: AdminEfpOrderCalculs;
  groupeCalculs?: AdminEfpOrderGroupeCalculs;
  groupePaysLivraison?: AdminEfpOrderPaysLivraison | null;
  groupeCodesPromo?: AdminEfpOrderPromoCode[];
  codesPromo?: AdminEfpOrderPromoCode[];
  poids?: number;
  selected: boolean;
  vendeur?: AdminEfpOrderVendeur;
  acheteur?: AdminEfpOrderAcheteur;
}

export interface AdminEfpOrdersPageResponse {
  items: AdminEfpOrder[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface AdminEfpVendeurListeItem {
  id_vendeur: number;
  nomBoutique?: string;
  nomSociete?: string;
  email?: string;
}

export interface AdminEfpDateFilterParams {
  dateDebut?: string;
  dateFin?: string;
  commandeMonth?: number;
  commandeDay?: number;
}

export const ADMIN_EFP_ORDER_ALLOWED_STATUSES = [
  1, 13, 4, 6, 7, 8, 14, 5, 11, 12, 2, 3,
] as const;

export const ADMIN_EFP_DATE_FILTER_START_YEAR = 2012;
export const ADMIN_EFP_ALL_PERIOD = 0;

export const ADMIN_EFP_ORDER_CARRIER_LABELS: Record<string, string> = {
  colissimo: "Colissimo",
  dpd: "DPD",
  ups: "UPS",
  gls: "GLS",
  chronopost: "Chronopost",
  dhl: "DHL",
  fedex: "Fedex",
  bollore: "Bolloré",
  transportAcheteur: "Transporteur Acheteur",
  UPSgroupage: "UPS ( Groupage )",
  UPSstandardGroupage: "UPS Standard ( Groupage )",
  FedexGroupage: "Fedex ( Groupage )",
  ChronopostGroupage: "Chrono Multi ( Groupage )",
  GLSmulti: "GLS ( Multi )",
  gpx: "GPX ( Groupage )",
  ocx: "OCX ( Groupage )",
};

export const ADMIN_EFP_MONTH_OPTIONS = [
  { value: 1, label: "Janvier" },
  { value: 2, label: "Février" },
  { value: 3, label: "Mars" },
  { value: 4, label: "Avril" },
  { value: 5, label: "Mai" },
  { value: 6, label: "Juin" },
  { value: 7, label: "Juillet" },
  { value: 8, label: "Août" },
  { value: 9, label: "Septembre" },
  { value: 10, label: "Octobre" },
  { value: 11, label: "Novembre" },
  { value: 12, label: "Décembre" },
] as const;

export const ADMIN_EFP_ORDER_STATUS_LABELS: Record<number, string> = {
  0: "En panier",
  1: "En attente de confirmation",
  2: "Commande expédiée",
  3: "Commande annulée",
  4: "Commande confirmée",
  5: "En attente de preuve de virement",
  6: "Préparation de groupage",
  7: "Pret pour pickup colis",
  8: "Pickup colis effectué",
  9: "En attente de confirmation",
  10: "Sauvegarder pour plus tard",
  11: "En attente de la validation du paiement",
  12: "Commande bloquée",
  13: "1ère commande",
  14: "Reçu au dépôt",
};

export const ADMIN_EFP_ORDER_STATUS_CLASSES: Record<number, string> = {
  0: "bg-gray-100 text-gray-800",
  1: "bg-yellow-100 text-yellow-800",
  2: "bg-purple-100 text-purple-800",
  3: "bg-red-100 text-red-800",
  4: "bg-green-100 text-green-800",
  5: "bg-orange-100 text-orange-800",
  6: "bg-blue-100 text-blue-800",
  7: "bg-indigo-100 text-indigo-800",
  8: "bg-green-100 text-green-800",
  9: "bg-yellow-100 text-yellow-800",
  10: "bg-gray-100 text-gray-800",
  11: "bg-orange-100 text-orange-800",
  12: "bg-red-100 text-red-800",
  13: "bg-blue-100 text-blue-800",
  14: "bg-[#DDA0DD] text-purple-900",
};
